import { DestroyRef, Injectable, inject } from '@angular/core';
import { ScreenAction, offerScreenActions } from '../services/mcp/actions/fast-lane';
import { ConfirmStep, offerConfirmSteps } from '../services/mcp/actions/other-confirm';

/** The tabs and panels of the screen on show, as the tools name them. */
export interface ScreenPanels {
  /** The route's screen, as `screenFromUrl` names it. */
  screen: string;
  here(): { tab?: string; panel?: string };
  /** Shows a tab or panel; false when it can't be shown yet or isn't on offer to this builder. */
  select(name: string): boolean;
  /** Why this tab or panel isn't on offer, when the screen knows; a name that merely hasn't arrived yet has no reason. */
  whyNot?(name: string): string | null;
  /**
   * What the screen holds now, by the action table's field names, for read_screen. The tool passes on only what the
   * table names and turns secret fields into set or not set, so a screen reports its state plainly.
   */
  holds?(): Record<string, unknown>;
  /** What leaving or filling this screen now would lose, for the unsaved check the tools make first; null when nothing. */
  unsaved?(): { count: number; name: string } | null;
  /** True while what `holds` reports is still arriving, so a blank there means "not here yet" rather than "empty". */
  loading?(): boolean;
}

export interface SettingsOverlay extends ScreenPanels {
  isOpen(): boolean;
  close(): void;
}

/** What a screen tells the tools about itself. Holds no state of its own and reaches nothing, so any screen can use it. */
@Injectable({ providedIn: 'root' })
export class ScreenPanelsService {
  current: ScreenPanels | null = null;
  /** Lives apart from `current` because Settings opens over a screen rather than replacing it. */
  settings: SettingsOverlay | null = null;
}

/** For a screen component's field initialiser: holds the slot for as long as the screen lives. */
export function registerScreenPanels(panels: ScreenPanels): ScreenPanels {
  const slot = inject(ScreenPanelsService);
  slot.current = panels;
  inject(DestroyRef).onDestroy(() => {
    if (slot.current === panels) slot.current = null;
  });
  return panels;
}

/** For a screen component's field initialiser: offers these to do_action for as long as the screen lives. */
export function registerScreenActions(actions: Record<string, ScreenAction>): Record<string, ScreenAction> {
  inject(DestroyRef).onDestroy(offerScreenActions(actions));
  return actions;
}

/** For a screen component's field initialiser: offers its confirm-lane steps for as long as the screen lives. */
export function registerConfirmSteps(steps: Record<string, ConfirmStep>): Record<string, ConfirmStep> {
  inject(DestroyRef).onDestroy(offerConfirmSteps(steps));
  return steps;
}
